import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Plus,
  Phone,
  Mail,
  MapPin,
  Building,
  Linkedin,
  Twitter,
  Calendar,
  MessageSquare,
  Users,
  FileText,
  Upload,
  Video,
  X
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import ContactForm from '../components/ContactForm';
import InteractionForm from '../components/InteractionForm';
import ConnectionForm from '../components/ConnectionForm';
import { format, formatDistanceToNow } from 'date-fns';

const INTERACTION_ICONS = {
  call: Phone,
  video_call: Video,
  meeting: Calendar,
  email: Mail,
  text: MessageSquare,
  social_media: MessageSquare,
  coffee: Calendar,
  event: Calendar,
  other: MessageSquare
};

function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    fetchContact,
    updateContact,
    deleteContact,
    fetchInteractions,
    createInteraction,
    deleteInteraction,
    fetchConnections,
    createConnection,
    deleteConnection,
    fetchDocuments,
    uploadDocument
  } = useApp();

  const [contact, setContact] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [connections, setConnections] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('interactions');
  const [showEditForm, setShowEditForm] = useState(false);
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [contactData, interactionsData, connectionsData, docsData] = await Promise.all([
      fetchContact(id),
      fetchInteractions(id),
      fetchConnections(id),
      fetchDocuments(id)
    ]);
    setContact(contactData);
    setInteractions(interactionsData);
    setConnections(connectionsData);
    setDocuments(docsData);
    setLoading(false);
  }, [id]); // Only depend on id, fetch functions are stable

  useEffect(() => {
    loadData();
  }, [id]); // Only reload when id changes

  const handleUpdate = async (data) => {
    await updateContact(id, data);
    loadData();
  };

  const handleDelete = async () => {
    await deleteContact(id);
    navigate('/contacts');
  };

  const handleAddInteraction = async (data) => {
    await createInteraction(data);
    loadData();
  };

  const handleDeleteInteraction = async (interactionId) => {
    if (window.confirm('Delete this interaction?')) {
      await deleteInteraction(interactionId);
      loadData();
    }
  };

  const handleAddConnection = async (data) => {
    await createConnection(data);
    loadData();
  };

  const handleDeleteConnection = async (connectionId) => {
    if (window.confirm('Remove this connection?')) {
      await deleteConnection(connectionId);
      loadData();
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      await uploadDocument(file, id, null, file.name);
      loadData();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="empty-state">
        <p>Contact not found</p>
        <Link to="/contacts" className="btn btn-primary mt-3">
          Back to Contacts
        </Link>
      </div>
    );
  }

  const getCategoryColor = (category) => {
    const colors = {
      family: '#ef4444',
      friends: '#22c55e',
      professional: '#3b82f6',
      acquaintance: '#6b7280'
    };
    return colors[category] || '#6b7280';
  };

  const getStrengthColor = (strength) => {
    if (strength >= 70) return 'var(--success)';
    if (strength >= 40) return 'var(--warning)';
    return 'var(--danger)';
  };

  const existingConnectionIds = connections.map(c => c.connected_contact?.id);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          className="btn btn-ghost"
          onClick={() => navigate('/contacts')}
          style={{ marginBottom: '1rem' }}
        >
          <ArrowLeft size={18} />
          Back to Contacts
        </button>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div className="avatar avatar-lg">
              {contact.first_name[0]}{contact.last_name?.[0] || ''}
            </div>
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>
                {contact.first_name} {contact.last_name}
              </h1>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span
                  className="badge"
                  style={{
                    background: `${getCategoryColor(contact.category)}15`,
                    color: getCategoryColor(contact.category),
                    textTransform: 'capitalize'
                  }}
                >
                  {contact.category}
                </span>
                {contact.tags?.map(tag => (
                  <span
                    key={tag.id}
                    className="tag"
                    style={{ background: `${tag.color}20`, color: tag.color }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="contact-header-actions">
            <button className="btn btn-secondary" onClick={() => setShowEditForm(true)}>
              <Edit2 size={16} />
              <span className="mobile-hide">Edit</span>
            </button>
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="contact-detail-layout">
        {/* Sidebar */}
        <div className="contact-detail-sidebar">
          {/* Contact Info */}
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '1rem' }}>Contact Info</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Multiple Emails */}
              {(contact.emails?.length > 0 || contact.email) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Mail size={16} className="text-muted" style={{ marginTop: '2px' }} />
                  <div>
                    {contact.emails?.length > 0 ? (
                      contact.emails.map((email, i) => (
                        <div key={i} style={{ marginBottom: i < contact.emails.length - 1 ? '0.25rem' : 0 }}>
                          <a href={`mailto:${email.value}`}>{email.value}</a>
                          <span className="text-xs text-muted" style={{ marginLeft: '0.5rem' }}>({email.type})</span>
                        </div>
                      ))
                    ) : (
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    )}
                  </div>
                </div>
              )}
              {/* Multiple Phones */}
              {(contact.phones?.length > 0 || contact.phone) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Phone size={16} className="text-muted" style={{ marginTop: '2px' }} />
                  <div>
                    {contact.phones?.length > 0 ? (
                      contact.phones.map((phone, i) => (
                        <div key={i} style={{ marginBottom: i < contact.phones.length - 1 ? '0.25rem' : 0 }}>
                          <a href={`tel:${phone.value}`}>{phone.value}</a>
                          <span className="text-xs text-muted" style={{ marginLeft: '0.5rem' }}>({phone.type})</span>
                        </div>
                      ))
                    ) : (
                      <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                    )}
                  </div>
                </div>
              )}
              {(contact.company || contact.job_title) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Building size={16} className="text-muted" />
                  <div>
                    {contact.company && <div>{contact.company}</div>}
                    {contact.job_title && (
                      <div className={contact.company ? "text-sm text-muted" : ""}>{contact.job_title}</div>
                    )}
                  </div>
                </div>
              )}
              {contact.address && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <MapPin size={16} className="text-muted" />
                  <span>{contact.address}</span>
                </div>
              )}
              {contact.birthday && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Calendar size={16} className="text-muted" />
                  <span>{format(new Date(contact.birthday), 'MMMM d, yyyy')}</span>
                </div>
              )}
              {contact.linkedin_url && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Linkedin size={16} className="text-muted" />
                  <div>
                    <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer">
                      LinkedIn Profile
                    </a>
                    {contact.linkedin_connected_date && (
                      <div className="text-xs text-muted">
                        Connected {format(new Date(contact.linkedin_connected_date), 'MMM d, yyyy')}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {contact.twitter_handle && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Twitter size={16} className="text-muted" />
                  <span>{contact.twitter_handle}</span>
                </div>
              )}
            </div>
          </div>

          {/* Relationship Strength */}
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '1rem' }}>Relationship</h3>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span className="text-sm">Strength</span>
                <span className="font-medium">{contact.relationship_strength}%</span>
              </div>
              <div className="strength-bar" style={{ height: '8px' }}>
                <div
                  className="strength-fill"
                  style={{
                    width: `${contact.relationship_strength}%`,
                    background: getStrengthColor(contact.relationship_strength)
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span className="text-sm text-muted">Contact Goal</span>
              <span className="text-sm">Every {contact.contact_frequency_goal} days</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="text-sm text-muted">Last Contact</span>
              <span className="text-sm">
                {contact.last_contacted
                  ? formatDistanceToNow(new Date(contact.last_contacted), { addSuffix: true })
                  : 'Never'}
              </span>
            </div>
          </div>

          {/* Context */}
          {(contact.how_we_met || contact.relationship_context || contact.interests || contact.notes) && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '1rem' }}>Context</h3>
              {contact.how_we_met && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div className="text-sm text-muted">How We Met</div>
                  <div>{contact.how_we_met}</div>
                </div>
              )}
              {contact.interests && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div className="text-sm text-muted">Interests</div>
                  <div>{contact.interests}</div>
                </div>
              )}
              {contact.relationship_context && (
                <div style={{ marginBottom: contact.notes ? '0.75rem' : 0 }}>
                  <div className="text-sm text-muted">Relationship Context</div>
                  <div>{contact.relationship_context}</div>
                </div>
              )}
              {contact.notes && (
                <div>
                  <div className="text-sm text-muted">Notes</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{contact.notes}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div>
          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '0.25rem',
            borderBottom: '1px solid var(--gray-200)',
            marginBottom: '1.5rem'
          }}>
            {['interactions', 'connections', 'documents'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: '2px solid',
                  borderColor: activeTab === tab ? 'var(--primary)' : 'transparent',
                  color: activeTab === tab ? 'var(--primary)' : 'var(--gray-500)',
                  fontWeight: '500',
                  textTransform: 'capitalize',
                  cursor: 'pointer'
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Interactions Tab */}
          {activeTab === 'interactions' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button className="btn btn-primary" onClick={() => setShowInteractionForm(true)}>
                  <Plus size={16} />
                  Log Interaction
                </button>
              </div>
              {interactions.length === 0 ? (
                <div className="card empty-state">
                  <MessageSquare size={32} className="empty-state-icon" />
                  <p>No interactions logged yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {interactions.map(interaction => {
                    const Icon = INTERACTION_ICONS[interaction.type] || MessageSquare;
                    return (
                      <div key={interaction.id} className="card">
                        <div style={{ display: 'flex', gap: '1rem' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: 'var(--gray-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <Icon size={20} className="text-muted" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div>
                                <div className="font-medium" style={{ textTransform: 'capitalize' }}>
                                  {interaction.type.replace('_', ' ')}
                                </div>
                                <div className="text-sm text-muted">
                                  {format(new Date(interaction.date), 'MMMM d, yyyy')}
                                  {interaction.duration_minutes && ` • ${interaction.duration_minutes} min`}
                                  {interaction.location && ` • ${interaction.location}`}
                                </div>
                              </div>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => handleDeleteInteraction(interaction.id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            {interaction.subject && (
                              <div style={{ marginTop: '0.5rem', fontWeight: '500' }}>
                                {interaction.subject}
                              </div>
                            )}
                            {interaction.notes && (
                              <div style={{ marginTop: '0.5rem' }} className="text-sm">
                                {interaction.notes}
                              </div>
                            )}
                            {interaction.sentiment && (
                              <div style={{ marginTop: '0.5rem' }}>
                                <span
                                  className="badge"
                                  style={{
                                    background: interaction.sentiment === 'positive' ? 'rgba(34, 197, 94, 0.1)' :
                                               interaction.sentiment === 'negative' ? 'rgba(239, 68, 68, 0.1)' :
                                               'var(--gray-100)',
                                    color: interaction.sentiment === 'positive' ? 'var(--success)' :
                                           interaction.sentiment === 'negative' ? 'var(--danger)' :
                                           'var(--gray-500)',
                                    textTransform: 'capitalize'
                                  }}
                                >
                                  {interaction.sentiment}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Connections Tab */}
          {activeTab === 'connections' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <button className="btn btn-primary" onClick={() => setShowConnectionForm(true)}>
                  <Plus size={16} />
                  Add Connection
                </button>
              </div>
              {connections.length === 0 ? (
                <div className="card empty-state">
                  <Users size={32} className="empty-state-icon" />
                  <p>No connections mapped yet</p>
                </div>
              ) : (
                <div className="grid grid-2">
                  {connections.map(connection => (
                    <div key={connection.id} className="card" style={{ position: 'relative' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleDeleteConnection(connection.id)}
                        style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}
                      >
                        <X size={14} />
                      </button>
                      <Link
                        to={`/contacts/${connection.connected_contact?.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                      >
                        <div className="avatar">
                          {connection.connected_contact?.first_name?.[0] || '?'}
                          {connection.connected_contact?.last_name?.[0] || ''}
                        </div>
                        <div>
                          <div className="font-medium">
                            {connection.connected_contact?.first_name} {connection.connected_contact?.last_name}
                          </div>
                          {connection.relationship_type && (
                            <div className="text-sm text-muted">{connection.relationship_type}</div>
                          )}
                        </div>
                      </Link>
                      {connection.notes && (
                        <div className="text-sm mt-2" style={{ paddingLeft: '52px' }}>
                          {connection.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                  <Upload size={16} />
                  {uploading ? 'Uploading...' : 'Upload Document'}
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                    disabled={uploading}
                  />
                </label>
              </div>
              {documents.length === 0 ? (
                <div className="card empty-state">
                  <FileText size={32} className="empty-state-icon" />
                  <p>No documents uploaded yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {documents.map(doc => (
                    <div key={doc.id} className="card" style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <FileText size={20} className="text-muted" />
                        <div style={{ flex: 1 }}>
                          <div className="font-medium">{doc.original_name}</div>
                          <div className="text-xs text-muted">
                            {format(new Date(doc.created_at), 'MMM d, yyyy')}
                            {doc.size && ` • ${(doc.size / 1024).toFixed(1)} KB`}
                          </div>
                        </div>
                        <a
                          href={`/api/documents/${doc.id}/download`}
                          className="btn btn-secondary btn-sm"
                        >
                          Download
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditForm && (
        <ContactForm
          contact={contact}
          onClose={() => setShowEditForm(false)}
          onSave={handleUpdate}
        />
      )}

      {showInteractionForm && (
        <InteractionForm
          contactId={id}
          onClose={() => setShowInteractionForm(false)}
          onSave={handleAddInteraction}
        />
      )}

      {showConnectionForm && (
        <ConnectionForm
          contactId={id}
          existingConnectionIds={existingConnectionIds}
          onClose={() => setShowConnectionForm(false)}
          onSave={handleAddConnection}
        />
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Delete Contact</h2>
              <button className="btn btn-ghost" onClick={() => setShowDeleteConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete {contact.first_name} {contact.last_name}?</p>
              <p className="text-sm text-muted mt-2">
                This will also delete all interactions and connections associated with this contact.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Delete Contact
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .contact-detail-layout {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 1.5rem;
        }
        .contact-detail-sidebar {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .contact-header-actions {
          display: flex;
          gap: 0.5rem;
        }
        @media (max-width: 768px) {
          .contact-detail-layout {
            grid-template-columns: 1fr;
          }
          .contact-detail-sidebar {
            order: 2;
          }
        }
      `}</style>
    </div>
  );
}

export default ContactDetail;
